import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";

import { PhotoUploadComponent } from "../../UserManagement/UserDetailsForm";
import { formInputStyles, formStyles } from "../../../shared/constants/styles";
import Switch from "../../../shared/components/form/Switch/Switch";
import {
  FormGroupLabel,
  FormItem,
  FormValueContainer,
  UnitSpan,
} from "../../../shared/components/form/FormElements";
import FormField from "../../../shared/components/form/FormField";
import { colorSwitchActive, colorYellowNew } from "../../../utils/palette";
import { getUnits, post, postApi } from "../../../api/getUser";
import {
  Desc,
  SingleValueOption,
} from "../../Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import Error from "@/shared/components/form/Error";
import CheckboxInputComponent from "../../../shared/components/form/CheckboxInputComponent";
import GroupsFormPopup from "../../Settings/Groups/GroupsFormPopup";
import InfoPopup, { InfoIcon } from "../../../shared/components/InfoPopup";
import SubscribePopup from "../../../shared/components/SubscribePopup";
import { useAuth } from "../../../contexts/AuthContext";
import SubscribeTooltipIcon from "../../../shared/components/SubscribeTooltipIcon";
import {
  getDistanceFactorforOdo,
  getTranslatedPMUnit,
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../../shared/helpers";
import {
  ActionIconContainer,
  AddFormButton,
  FilterButton,
  LinkedVehicleElement,
} from "../../../shared/components/TableElements";
import VehicleDetailsPopup from "../../../shared/components/VehicleDetailsPopup";

import { s3Url } from "../../../shared/constants";
import CustomMenuList from "../../../shared/components/form/CustomMenuList";
import CreateEquipmentTypePopup from "./CreateEquipmentTypePopup";
import defaultVehicleIcon from "@/shared/img/defaultVehicleIcon.png";

const EquipmentDetailsForm = ({
  rowData,
  type,
  userAssignedUpdated,
  setUserAssignedUpdated,
  setUsersAssigned,
  vehiclePhoto,
  setVehiclePhoto,
  setActiveKey,
}) => {
  const {
    handleSubmit,
    reset,
    register,
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const { t } = useTranslation("common");
  const { currency, distance } = useSelector((state) => state.units);

  const allUsers = useSelector((state) => state.users.list);
  const allGroups = useSelector((state) => state.groups.list);

  const { userId, roleId, country, orgId } = useSelector((state) => state.user);

  const { tag } = useAuth();

  const [groups, setGroups] = useState(allGroups?.slice(1));

  //groups will be disabled for a manager that has no group_ids
  const [disableGroups, setDisableGroups] = useState(false);

  const groupChangePending = useRef({ val: null, onChangeFunc: null });

  //Hook form variables
  const equipmentName = watch("equipmentName");
  const licenseNo = watch("licenseNo");
  const equipmentType = watch("equipmentType");
  const vin = watch("vin");
  const status = watch("status");

  const make = watch("make");
  const group = watch("group");
  const model = watch("model");
  const year = watch("year");
  //const fuelType = watch("fuelType");
  const primaryMeter = watch("primaryMeter");
  const purchasePrice = watch("purchasePrice");
  const startingOdo = watch("startingOdo");
  const outOfService = watch("outOfService");
  const currentOdo = watch("currentOdo"); // odo that can be overridden

  const linkedVehicle = watch("linkedVehicle");

  //States for Adding new group
  const [newGroupInfo, setNewGroupInfo] = useState(null);
  const [showAddGroupPopup, setShowAddGroupPopup] = useState(false);

  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([
    { label: t("lawn_mower"), value: t("lawn_mower") },
    { label: t("generator"), value: t("generator") },
    { label: t("barcode_scanner"), value: t("barcode_scanner") },
    { label: t("pallet_jack"), value: t("pallet_jack") },
    { label: t("hand_truck"), value: t("hand_truck") },
    { label: t("air_compressor"), value: t("air_compressor") },
    { label: t("pressure_washer"), value: t("pressure_washer") },
    { label: t("portable_pump"), value: t("portable_pump") },
    { label: t("leaf_blower"), value: t("leaf_blower") },
    { label: t("chainsaw"), value: t("chainsaw") },
  ]);

  const [primaryMeterOptions, setPrimaryMeterOptions] = useState([]);

  //To show a popup that inactivating a vehicle will delete it's reminders
  const [showEquipmentInactivePopup, setShowEquipmentInactivePopup] =
    useState(false);

  //State to show subscribe popup
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);

  const isDisabled = type === "edit" && !status;

  //State to toggle popup when group is changed
  //to show user the message the editing the group will dissociate prev assigned users
  //to the vehicle
  const [showGroupChangeWarnPopup, setShowGroupChangeWarnPopup] =
    useState(false);

  const [showCreateEquipmentTypePopup, setShowCreateEquipmentTypePopup] =
    useState(false);
  const [equipmentTypePopupErr, setEquipmentTypePopupErr] = useState("");

  const [showLinkedVehicleDetails, setShowLinkedVehicleDetails] =
    useState(false);

  //To show linked vehicle img
  let linkedVehicleImgUrl = "";
  if (linkedVehicle) {
    linkedVehicleImgUrl = linkedVehicle?.veh_img
      ? `${s3Url}/org_${orgId}/vehicle_${linkedVehicle.veh_id}/${linkedVehicle.veh_img}`
      : defaultVehicleIcon;
  }

  //Initial Effect
  useEffect(() => {
    //Logic to fetch custom vehicle types
    (async () => {
      const res = await postApi(
        { querystring: "get_equipment_types" },
        "commonNew"
      );

      if (res.success) {
        const { user_data } = res;

        //Set default type (Truck)
        // if (type !== "edit") {
        //   setValue("vehicleType", {
        //     label: t("veh_type_value_truck"),
        //     value: t("veh_type_value_truck"),
        //   });
        // }

        setEquipmentTypeOptions((prev) => {
          const temp = [...prev];
          user_data.forEach((obj) => {
            temp.push({
              label: obj.veh_type,
              value: obj.veh_type,
            });
          });
          return temp;
        });
      }
    })();

    //Logic to get units
    (async () => {
      const unitsRes = await getUnits();
      if (unitsRes.success) {
        const {
          meta_data: { units },
        } = unitsRes;

        let distanceUnits = units
          .filter((u) => u.unit_type === "distance")
          .map((u) => ({
            ...u,
            label: getTranslatedPMUnit(u.unit_name, t),
            value: u.unit_name,
          }));

        if (type !== "edit") {
          setValue(
            "primaryMeter",
            { label: t("hours_const"), value: "Hours" }
            // distanceUnits.find((d) => d.value === distance)
          );
        }

        setPrimaryMeterOptions(distanceUnits);
      }
    })();
  }, []);

  //Effect to set groups
  //Admin will be shown all groups
  //Manager will be shown groups that are assigned to him
  useEffect(() => {
    if (allGroups?.length) {
      let groupOptions = allGroups.slice(1);
      //For managers, show them only the groups that are assigned to them
      if (roleId === 2) {
        const currentUser = allUsers.find((u) => u.user_id === userId);
        const assignedGroupIds = currentUser?.group_ids;
        if (assignedGroupIds) {
          const assignedGroupIdsArr = JSON.parse(assignedGroupIds);
          if (assignedGroupIdsArr?.length) {
            groupOptions = groupOptions.filter((g) =>
              assignedGroupIdsArr.includes(g.group_id)
            );
          } else {
            // if group_ids is '[]'
            groupOptions = [];
            setDisableGroups(true);
          }
        } else {
          // if group_ids is null
          groupOptions = [];
          setDisableGroups(true);
        }
      }
      setGroups(groupOptions);
    }
  }, [allGroups, allUsers, userId, roleId]);

  //Effect to update fields on edit
  useEffect(() => {
    if (type === "edit" && rowData) {
      const {
        veh_id,
        name,
        license_no,
        make,
        model,
        veh_type,
        group_id,
        year,
        //fuel_type,
        veh_active,
        vin_no,
        primary_meter,
        starting_odo,
        veh_img,
        secondary_status,
        purchase_price,
        org_id,
        odo,
      } = rowData;

      setValue("equipmentName", name);
      setValue("licenseNo", license_no);
      setValue("equipmentType", {
        label: veh_type,
        value: veh_type,
      });
      setValue("vin", vin_no);
      setValue("make", make);
      setValue("model", model);
      setValue("year", year || "");
      setValue(
        "group",
        allGroups?.find((g) => g.group_id === group_id) || null
      );
      //Setting Status
      if (veh_active === 1) {
        setValue("status", true);
      } else {
        setValue("status", false);
      }
      // if (fuel_type) {
      //   setValue(
      //     "fuelType",
      //     fuelTypeOptions.find((f) => f.value === fuel_type)
      //   );
      // } else {
      //   setValue("fuelType", fuelTypeOptions[1]);
      // }
      setValue(
        "primaryMeter",
        primaryMeterOptions.find((f) => f.value === primary_meter)
      );

      const distanceFactor = getDistanceFactorforOdo(primary_meter, distance);
      const odoUpd = starting_odo
        ? (+starting_odo / distanceFactor)?.toFixed(2)
        : "";
      setValue("startingOdo", odoUpd);
      setValue("purchasePrice", purchase_price);

      setValue("outOfService", secondary_status === 1);
      //setOutOfService(secondary_status === 1);

      //Logic to set Vehicle Image
      if (veh_img) {
        const imageUrl = `${s3Url}/org_${org_id}/vehicle_${veh_id}/${veh_img}`;

        setVehiclePhoto({ url: imageUrl, imgName: veh_img });
      } else {
        setVehiclePhoto("");
      }

      const currentOdoConverted = odo ? odo?.toFixed(2) : 0;
      setValue("currentOdo", currentOdoConverted);
    }
  }, [rowData, primaryMeterOptions]);

  //If out of service is checked, set status to Inactive
  useEffect(() => {
    if (outOfService) {
      //setStatus(false);
      setValue("status", true);
    }
    // else {
    //   setValue("status", true);
    // }
  }, [outOfService]);

  //Set newly created group from Add Group Popup
  useEffect(() => {
    if (newGroupInfo?.id) {
      const newGroup = allGroups?.find((g) => g.group_id === newGroupInfo?.id);
      if (newGroup) {
        setNewGroupInfo(null);
      }
      setValue("group", newGroup);
    }
  }, [newGroupInfo?.id, allGroups]);

  //Handler for creating new Vehicle Type
  const handleCreateEquipmentType = (data) => {
    const newType = { label: data, value: data };
    setEquipmentTypeOptions((prev) => {
      let temp = [...prev];
      temp.push(newType);
      return temp;
    });
    setValue("equipmentType", newType);
  };

  //Handler for creating new Vehicle Type from popup
  const handleCreateEquipmentTypeFromPopup = (data) => {
    const newEquipmentType = data?.trim();
    const newType = { label: newEquipmentType, value: newEquipmentType };

    if (
      equipmentTypeOptions?.find(
        (t) =>
          t.value?.trim()?.toLocaleLowerCase() ===
          newType.value?.trim()?.toLocaleLowerCase()
      )
    ) {
      setEquipmentTypePopupErr(t("equipment_type_already_exists_err"));
      return;
    } else {
      setEquipmentTypePopupErr("");
    }
    setValue("equipmentType", newType);
    setEquipmentTypeOptions((prev) => {
      let temp = [...prev];
      temp.push(newType);
      return temp;
    });
    setShowCreateEquipmentTypePopup(false);
  };

  //Handler for creating new group
  const handleCreateGroup = (data) => {
    setNewGroupInfo({ name: data, id: "" });
    setShowAddGroupPopup(true);
  };

  //Handler for toggling switch
  const handleSwitchToggle = (onChange, value) => {
    if (!value) {
      //Unlink linked vehicle, if any
      if (linkedVehicle) {
        setValue("linkedVehicle", null);
      }

      if (outOfService) {
        setValue("outOfService", false);
        //setOutOfService(false);
      }
      setShowEquipmentInactivePopup(true);
    }

    //Remove vehicles assigned when user becomes inactive
    if (!value) {
      if (roleId === 2) {
        setUsersAssigned(allUsers.filter((u) => u.user_id === userId));
      } else {
        setUsersAssigned([]);
      }
      if (!userAssignedUpdated) {
        setUserAssignedUpdated(true);
      }
    }
    onChange(value);
  };

  const handleGroupChange = (e, onChange) => {
    setUsersAssigned((prev) => {
      let temp = [...prev];
      // if (type !== "edit") {
      //   temp = [];
      // }
      if (status) {
        //Remove users belonging to prev selected group
        if (group) {
          // group here is previously selected group
          temp = temp.filter((user) => {
            const groupIds = user.group_ids ? JSON.parse(user.group_ids) : [];
            if (
              !groupIds.length ||
              !groupIds.includes(group.group_id)
              //||
              //(roleId === 2 && user.user_id === userId) //For manager login, make sure he is still assigned even if he is part of prev group
            ) {
              return user;
            }
          });
        }

        //Add users belonging to new group
        if (e) {
          allUsers.forEach((user) => {
            const groupIds = user.group_ids ? JSON.parse(user.group_ids) : [];
            if (
              user.user_active === 1 &&
              (user.role_id == 2 || user.role_id == 3) &&
              groupIds.length &&
              groupIds.includes(e.group_id) &&
              temp.every((u) => u.user_id !== user.user_id)
            ) {
              temp.push(user);
            }
          });
        }

        //For manager login, if no users assigned, then assign manager
        if (roleId === 2 && !temp.length) {
          temp = allUsers.filter((u) => u.user_id === userId);
        }
      } else {
        if (roleId === 2) {
          temp = allUsers.filter((u) => u.user_id === userId);
        }
      }

      return temp;
    });

    onChange(e);

    // if (status) {
    //   let usersAssignedToGroup = [];
    //   if (allUsers?.length) {
    //     if (e) {
    //       allUsers.forEach((user) => {
    //         const groupIds = user.group_ids ? JSON.parse(user.group_ids) : [];
    //         if (
    //           user.user_active === 1 &&
    //           groupIds.length &&
    //           groupIds.includes(e.group_id)
    //           //&& usersAssignedToGroup.every((u) => u.user_id !== user.user_id)
    //         ) {
    //           usersAssignedToGroup.push(user);
    //         }
    //       });
    //     } else {
    //       if (roleId === 2) {
    //         usersAssignedToGroup = allUsers.filter((u) => u.user_id === userId);
    //       }
    //     }
    //   }
    //   setUsersAssigned(usersAssignedToGroup);
    // } else {
    //   if (roleId === 2) {
    //     setUsersAssigned(allUsers.filter((u) => u.user_id === userId));
    //   } else {
    //     setUsersAssigned([]);
    //   }
    // }

    if (showGroupChangeWarnPopup) {
      setShowGroupChangeWarnPopup(false);
    }
    // if (type === "edit") {
    //   setShowGroupChangeWarnPopup(true);
    // }
  };

  const onGroupChange = (e, onChange) => {
    if (type === "edit" && group?.group_id !== e?.group_id) {
      groupChangePending.current = {
        val: e,
        onChangeFunc: onChange,
      };
      setShowGroupChangeWarnPopup(true);
    } else {
      handleGroupChange(e, onChange);
    }
  };

  return (
    <>
      {showCreateEquipmentTypePopup && (
        <CreateEquipmentTypePopup
          show={showCreateEquipmentTypePopup}
          onClose={() => {
            setEquipmentTypePopupErr("");
            setShowCreateEquipmentTypePopup(false);
          }}
          onAddEquipmentType={handleCreateEquipmentTypeFromPopup}
          equipmentTypePopupErr={equipmentTypePopupErr}
        />
      )}
      {showEquipmentInactivePopup && (
        <InfoPopup
          showPopup={showEquipmentInactivePopup}
          setShowPopup={setShowEquipmentInactivePopup}
          message={t("inactive_equipment_delete_reminders_msg")}
          icon={{
            color: colorYellowNew,
            className: "fi fi-rr-triangle-warning",
          }}
        />
      )}
      {showAddGroupPopup && (
        <GroupsFormPopup
          showPopup={showAddGroupPopup}
          setShowPopup={setShowAddGroupPopup}
          newGroupInfo={newGroupInfo}
          setNewGroupInfo={setNewGroupInfo}
          mode={"from-add-vehicle"}
        />
      )}
      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={t("subscribe_msg")}
        />
      )}
      {showGroupChangeWarnPopup && (
        // <InfoPopup
        //   showPopup={showGroupChangeWarnPopup}
        //   setShowPopup={setShowGroupChangeWarnPopup}
        //   message={t("changing_group_will_remove_old_group_users_msg")}
        //   icon={{
        //     color: colorYellowNew,
        //     className: "fi fi-rr-triangle-warning",
        //   }}
        // />
        <ConfirmChangeGroupPopup
          showPopup={showGroupChangeWarnPopup}
          setShowPopup={setShowGroupChangeWarnPopup}
          handleYes={() =>
            handleGroupChange(
              groupChangePending.current.val,
              groupChangePending.current.onChangeFunc
            )
          }
          handleNo={() => setShowGroupChangeWarnPopup(false)}
          message={t("changing_group_will_remove_old_group_users_msg")}
        />
      )}
      {showLinkedVehicleDetails && (
        <VehicleDetailsPopup
          showPopup={showLinkedVehicleDetails}
          setShowPopup={setShowLinkedVehicleDetails}
          vehicleId={linkedVehicle?.veh_id}
        />
      )}
      <Container className="pt-2 pb-4">
        <Row>
          <Col md={2} className="pt-2 ">
            <PhotoUploadComponent
              photo={vehiclePhoto}
              setPhoto={setVehiclePhoto}
              rowData={rowData}
              isDisabled={isDisabled}
            />
          </Col>
          <Col md={8}>
            {/* {type === "edit" &&
              rowData?.veh_active == 0 &&
              rowData?.secondary_status == 2 && (
                <Row className="pt-2">
                  <Col>
                    <Desc>{t("veh_imported_from_telematics_msg")}</Desc>
                  </Col>
                </Row>
              )} */}
            <Row>
              <Col md={5}>
                <FormGroupLabel>{t("equipment_name")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="equipmentName"
                    control={control}
                    component="input"
                    rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("equipment_name")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.equipmentName && (
                  <Error error={errors.equipmentName.message} />
                )}
              </Col>
              <Col md={2}></Col>
              <Col md={5}>
                <FormGroupLabel>{t("license_number")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="licenseNo"
                    control={control}
                    component="input"
                    //rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("license_number")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.licenseNo && (
                  <Error error={errors.licenseNo.message} />
                )}
              </Col>
            </Row>

            <Row>
              <Col md={5}>
                <FormGroupLabel>{t("equipment_type")}</FormGroupLabel>
                <Controller
                  name="equipmentType"
                  //defaultValue={vehicleTypeOptions[0]}
                  control={control}
                  //errors={errors}
                  render={({ field: { onChange, value, ref } }) => (
                    <CreatableSelect
                      options={equipmentTypeOptions}
                      //value={vehicles.find((c) => c.value === value)}
                      value={value}
                      onChange={onChange}
                      onCreateOption={handleCreateEquipmentType}
                      placeholder={t("equipment_type_placeholder")}
                      styles={formStyles}
                      isDisabled={isDisabled}
                      components={{
                        Option: SingleValueOption,
                        MenuList: CustomMenuList,
                      }}
                      onCreateNew={() => setShowCreateEquipmentTypePopup(true)}
                      createNewLabel={t("create_new_equipment_type")}
                      id={"equipment-type"}
                      customProps={{ type: "equipmentType" }}
                    />
                  )}
                  rules={{ required: t("required_err") }}
                />

                {errors?.equipmentType && (
                  <Error error={errors.equipmentType.message} />
                )}
              </Col>
              <Col md={2}></Col>

              <Col md={5}>
                <FormGroupLabel>{t("vin")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="vin"
                    control={control}
                    component="input"
                    //rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("vin")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.vin && <Error error={errors.vin.message} />}
              </Col>
            </Row>

            <Row>
              <Col md={5}>
                <FormGroupLabel>{t("make")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="make"
                    control={control}
                    component="input"
                    rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("make")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.make && <Error error={errors.make.message} />}
              </Col>
              <Col md={2}></Col>

              <Col md={5}>
                <FormGroupLabel>
                  {t("group")}
                  {tag === "free-user" && (
                    <SubscribeTooltipIcon message={t("subscribe_msg")} />
                  )}
                </FormGroupLabel>
                <Controller
                  name="group"
                  control={control}
                  //errors={errors}
                  render={({ field: { onChange, value, ref } }) => (
                    <CreatableSelect
                      options={groups}
                      onMenuOpen={() => {
                        if (tag === "free-user") {
                          setShowSubscribePopup(true);
                          return;
                        }
                      }}
                      //value={vehicles.find((c) => c.value === value)}
                      value={value}
                      //onChange={(e) => handleGroupChange(e, onChange)}
                      onChange={(e) => onGroupChange(e, onChange)}
                      onCreateOption={handleCreateGroup}
                      placeholder={t("group_placeholder")}
                      styles={formStyles}
                      isDisabled={isDisabled || disableGroups}
                      components={{
                        Option: SingleValueOption,
                        MenuList: CustomMenuList,
                      }}
                      customProps={{ type: "group" }}
                      isClearable
                      onCreateNew={() => handleCreateGroup("")}
                      createNewLabel={t("create_new_group")}
                    />
                  )}
                  //rules={{ required: t("required_err") }}
                />

                {errors?.group && <Error error={errors.group.message} />}
              </Col>
            </Row>

            <Row>
              <Col md={5}>
                <FormGroupLabel>{t("model")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="model"
                    control={control}
                    component="input"
                    rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("model")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.model && <Error error={errors.model.message} />}
              </Col>
              <Col md={2}></Col>

              <Col md={5}>
                <FormGroupLabel>{t("status")}</FormGroupLabel>
                <Controller
                  name="status"
                  control={control}
                  //errors={errors}
                  defaultValue={true}
                  render={({ field: { onChange, value, ref } }) => (
                    <div
                      style={{ height: "34px" }}
                      className="d-flex align-items-center gap-2"
                    >
                      <Switch
                        id={"equipment-status-switch"}
                        isOn={value}
                        onColor={colorSwitchActive}
                        handleToggle={(e) =>
                          handleSwitchToggle(onChange, e.target.checked)
                        }
                      />
                      <span>{status ? t("active") : t("inactive")}</span>
                    </div>
                  )}
                  //rules={{ required: t("required_err") }}
                />
              </Col>
            </Row>

            <Row>
              <Col md={5}>
                <FormGroupLabel>{t("year")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="year"
                    control={control}
                    component="input"
                    //rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("year")}
                    style={formInputStyles}
                    type="number"
                    onKeyDown={handleNegative}
                    onWheel={handleNumberInputOnWheelPreventChange}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.year && <Error error={errors.year.message} />}
              </Col>
              <Col md={2}></Col>

              <Col>
                <FormValueContainer style={{ paddingTop: "3.5rem" }}>
                  <Col md={12}>
                    <Controller
                      name="outOfService"
                      control={control}
                      render={({ field: { onChange, value, ref } }) => (
                        <CheckboxInputComponent
                          label={t("equipment_out_of_service_label")}
                          checked={value}
                          onChange={onChange}
                          id={"out-of-service-label"}
                        />
                      )}
                    />

                    {errors?.outOfService && (
                      <Error error={errors.outOfService.message} />
                    )}
                  </Col>
                </FormValueContainer>
              </Col>
            </Row>

            <Row>
              {/* <Col md={5}>
                <FormGroupLabel>{t("fuel_type")}</FormGroupLabel>
                <Controller
                  name="fuelType"
                  control={control}
                  //defaultValue={fuelTypeOptions[1]}
                  //errors={errors}
                  render={({ field: { onChange, value, ref } }) => (
                    <Select
                      options={fuelTypeOptions}
                      //value={vehicles.find((c) => c.value === value)}
                      value={value}
                      onChange={onChange}
                      //onCreateOption={handleCreate}
                      placeholder={t("fuel_type")}
                      styles={formStyles}
                      isDisabled={isDisabled}
                    />
                  )}
                  rules={{ required: t("required_err") }}
                />

                {errors?.fuelType && <Error error={errors.fuelType.message} />}
              </Col>
              <Col md={2}></Col> */}

              <Col md={5}>
                <FormGroupLabel>{t("primary_meter")}</FormGroupLabel>
                <Controller
                  name="primaryMeter"
                  control={control}
                  //errors={errors}
                  render={({ field: { onChange, value, ref } }) => (
                    <Select
                      options={primaryMeterOptions}
                      //value={vehicles.find((c) => c.value === value)}
                      value={value}
                      onChange={onChange}
                      //onCreateOption={handleCreate}
                      placeholder={t("primary_meter")}
                      styles={formStyles}
                      isDisabled={isDisabled}
                    />
                  )}
                  rules={{ required: t("required_err") }}
                />

                {errors?.primaryMeter && (
                  <Error error={errors.primaryMeter.message} />
                )}
              </Col>
              <Col md={2}></Col>
              <Col md={5}>
                <FormGroupLabel>{t("purchase_price")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="purchasePrice"
                    control={control}
                    component="input"
                    type="number"
                    onKeyDown={handleNegative}
                    onWheel={handleNumberInputOnWheelPreventChange}
                    //rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("purchase_price")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                  <UnitSpan>{currency}</UnitSpan>
                </FormItem>

                {errors?.purchasePrice && (
                  <Error error={errors.purchasePrice.message} />
                )}
              </Col>
              {/* <Col md={5}>
                <FormGroupLabel>{t("starting_odo")}</FormGroupLabel>
                <FormItem
                  disabled={isDisabled}
                  //tabIndex={0}
                >
                  <FormField
                    name="startingOdo"
                    control={control}
                    component="input"
                    type="number"
                    onKeyDown={handleNegative}
                    onWheel={handleNumberInputOnWheelPreventChange}
                    //rules={{ required: t("required_err") }}
                    defaultValue=""
                    //isAboveError={isAboveError}
                    placeholder={t("starting_odo")}
                    style={formInputStyles}
                    disabled={isDisabled}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.startingOdo && (
                  <Error error={errors.startingOdo.message} />
                )}
              </Col> */}
            </Row>

            <hr className="mt-4 mb-1" />
            <Row>
              <Col>
                <FormGroupLabel>{t("linked_vehicle")}</FormGroupLabel>
                <div className="d-flex gap-3 align-items-center">
                  {linkedVehicle ? (
                    <>
                      {linkedVehicle?.value ? (
                        <div className="d-flex gap-2 align-items-center">
                          <img
                            src={linkedVehicleImgUrl}
                            alt="vehicle-img"
                            style={{
                              width: "20px",
                              height: "20px",
                              objectFit: "cover",
                              border: `1px solid #009BD9`,
                              background: "#D4E5F6",
                              //filter: isInactiveVehicle ? "grayscale(100%)" : "none",
                              padding: "2px",
                            }}
                            className="rounded-circle"
                          />
                          <LinkedVehicleElement
                            onClick={() => setShowLinkedVehicleDetails(true)}
                          >
                            {linkedVehicle?.name}
                          </LinkedVehicleElement>
                        </div>
                      ) : (
                        <p>{t("unassigned_asset")}</p>
                      )}
                    </>
                  ) : (
                    <span>{t("no_vehicle_linked")}</span>
                  )}
                  <FilterButton
                    onClick={() => setActiveKey("3")}
                    type="button"
                    style={{ minWidth: "min-content" }}
                    disabled={!status}
                  >
                    {t("manage")}
                  </FilterButton>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EquipmentDetailsForm;

const ConfirmChangeGroupPopup = ({
  showPopup,
  setShowPopup,
  message,
  handleYes,
  handleNo,
  modalSize,
}) => {
  const { t } = useTranslation("common");
  return (
    <Modal
      show={showPopup}
      onHide={() => setShowPopup(!showPopup)}
      centered
      size={modalSize ? modalSize : "sm"}
    >
      <Modal.Body>
        <div
          className="d-flex justify-content-end"
          style={{ width: "-webkit-fill-available" }}
        >
          <ActionIconContainer
            onClick={() => setShowPopup(false)}
            id="close-action-popup-icon-container"
          >
            <i className="fi fi-rr-cross-small" id="close-action-popup-icon" />
          </ActionIconContainer>
        </div>
        <div className="d-flex gap-3 justify-content-start align-items-center p-2">
          <InfoIcon
            className={"fi fi-rr-triangle-warning"}
            color={colorYellowNew}
            id="info-icon"
          />
          <p style={{ fontSize: "16px" }} id="info-msg">
            {message}
          </p>
        </div>
        <div className="d-flex justify-content-center align-items-center p-2 gap-3">
          <FilterButton onClick={handleNo} type="button" id="no-action-btn">
            {t("cancel")}
          </FilterButton>
          <AddFormButton onClick={handleYes} type="button" id="yes-action-btn">
            {t("ok")}
          </AddFormButton>
        </div>
      </Modal.Body>
    </Modal>
  );
};
